import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { USER_STATUS_LABELS, VERIFICATION_LABELS } from '@kulum/shared';
import {
  VERIFICATION_KEYS,
  fetchReviewDetail,
  submitReviewDecision,
  type ReviewDetail,
  type ReviewDecision,
} from '@/lib/adminReviewService';
import { useAuthStore } from '@/stores/authStore';
import { Loading } from '@/components/ui/Loading';

const roleLabels: Record<ReviewDetail['role'], string> = {
  donor: 'ドナー',
  donee: 'ドニー',
  unknown: '不明',
};

const userStatusLabels: Record<ReviewDetail['userStatus'], string> = {
  pending: USER_STATUS_LABELS.pending,
  email_verified: USER_STATUS_LABELS.email_verified,
  under_review: USER_STATUS_LABELS.under_review,
  approved: USER_STATUS_LABELS.approved,
  rejected: USER_STATUS_LABELS.rejected,
  suspended: USER_STATUS_LABELS.suspended,
  withdrawn: USER_STATUS_LABELS.withdrawn,
  unknown: '不明',
};

const reviewStatusLabels = {
  pending: '審査待ち',
  approved: '承認済み',
  rejected: '却下済み',
  remanded: '差し戻し',
} as const;
const rankOptions = ['A', 'B', 'C', 'D'] as const;

const formatDateTime = (date?: Date): string => {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDate = (date?: Date): string => {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const resolveStatusClass = (status: string): string => {
  if (status === 'approved') {
    return 'status-pill approved';
  }
  if (status === 'rejected') {
    return 'status-pill rejected';
  }
  if (status === 'remanded') {
    return 'status-pill remanded';
  }
  if (status === 'pending') {
    return 'status-pill pending';
  }
  return 'status-pill none';
};

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : '審査処理に失敗しました';

const isImageDocument = (type?: string, fileName?: string): boolean => {
  if (type?.startsWith('image/')) {
    return true;
  }
  return /\.(jpe?g|png|gif|webp)$/i.test(fileName ?? '');
};

interface ReviewDetailPanelProps {
  userId?: string;
  embedded?: boolean;
  onClose?: () => void;
  onReviewed?: () => void;
}

export function ReviewDetailPanel({
  userId,
  embedded = false,
  onClose,
  onReviewed,
}: ReviewDetailPanelProps) {
  const navigate = useNavigate();
  const adminProfile = useAuthStore((state) => state.adminProfile);

  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [checks, setChecks] = useState({
    birthDateMatched: false,
    incomeMatched: false,
    educationMatched: false,
    stdTestNegative: false,
  });
  const [ranks, setRanks] = useState({
    education: '',
    income: '',
    spermTest: '',
    stdTest: '',
  });
  const [actionError, setActionError] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!userId) {
      setError('ユーザーIDが不正です');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const reviewDetail = await fetchReviewDetail(userId);
      setDetail(reviewDetail);
      setComment(reviewDetail.adminReview?.comment ?? '');
      setChecks({
        birthDateMatched: reviewDetail.adminReview?.checks?.birthDateMatched ?? false,
        incomeMatched: reviewDetail.adminReview?.checks?.incomeMatched ?? false,
        educationMatched: reviewDetail.adminReview?.checks?.educationMatched ?? false,
        stdTestNegative: reviewDetail.adminReview?.checks?.stdTestNegative ?? false,
      });
      setRanks({
        education:
          reviewDetail.adminReview?.ranks?.education ??
          reviewDetail.adminReview?.ranks?.educationInternal ??
          '',
        income: reviewDetail.adminReview?.ranks?.income ?? '',
        spermTest: reviewDetail.adminReview?.ranks?.spermTest ?? '',
        stdTest: reviewDetail.adminReview?.ranks?.stdTest ?? '',
      });
    } catch (loadError) {
      console.error('Failed to fetch review detail:', loadError);
      setError(getErrorMessage(loadError));
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const canReview = useMemo(
    () => detail?.adminReview?.status === 'pending',
    [detail?.adminReview?.status]
  );

  const handleDecision = async (decision: ReviewDecision) => {
    if (!detail || !adminProfile) {
      return;
    }

    const normalizedComment = comment.trim();
    if ((decision === 'rejected' || decision === 'remanded') && normalizedComment.length === 0) {
      setActionError(decision === 'rejected' ? '却下する場合はコメントを入力してください' : '差し戻しする場合はコメントを入力してください');
      return;
    }

    const isDonor = detail.role === 'donor';
    if (decision === 'approved') {
      if (!checks.birthDateMatched) {
        setActionError('承認には「生年月日の照合OK」が必要です');
        return;
      }
      if (isDonor) {
        if (!checks.incomeMatched || !checks.educationMatched || !checks.stdTestNegative) {
          setActionError('承認には年収・学歴・性病検査の確認を完了してください');
          return;
        }
        if (!ranks.education || !ranks.income || !ranks.spermTest || !ranks.stdTest) {
          setActionError('承認には学歴・年収・精液検査・性病検査のランク入力が必要です');
          return;
        }
      }
    }

    const confirmMessage =
      decision === 'approved'
        ? 'この申請を承認しますか？'
        : decision === 'remanded'
        ? 'この申請を差し戻しますか？（コメントは申請者に表示されます）'
        : 'この申請を却下しますか？（却下後は1ヶ月再申請できません）';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setActionError(null);
    setIsSubmitting(true);

    try {
      await submitReviewDecision({
        userId: detail.userId,
        reviewerId: adminProfile.uid,
        decision,
        comment: normalizedComment,
        checks,
        ranks,
      });
      if (embedded) {
        await loadDetail();
        onReviewed?.();
        setIsSubmitting(false);
        return;
      }
      navigate('/reviews/pending', { replace: true });
    } catch (submitError) {
      console.error('Failed to submit review decision:', submitError);
      setActionError(getErrorMessage(submitError));
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loading message="審査詳細を読み込んでいます..." />;
  }

  if (error || !detail) {
    return (
      <section className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <h2 className="text-lg font-semibold text-red-800">審査詳細を表示できませんでした</h2>
        <p className="mt-2 text-sm text-red-700">{error ?? 'データの取得に失敗しました'}</p>
        {embedded ? (
          <button
            type="button"
            onClick={onClose}
            className="mt-4 inline-flex rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700"
          >
            閉じる
          </button>
        ) : (
          <Link
            to="/reviews/pending"
            className="mt-4 inline-flex rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700"
          >
            一覧に戻る
          </Link>
        )}
      </section>
    );
  }

  return (
    <section className={embedded ? 'space-y-5 p-5' : 'space-y-5'}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {embedded ? (
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-admin-accent hover:underline"
            >
              閉じる
            </button>
          ) : (
            <Link to="/reviews/pending" className="text-sm font-medium text-admin-accent hover:underline">
              ← 審査待ち一覧に戻る
            </Link>
          )}
          <h2 className="mt-2 text-2xl font-bold text-admin-ink">
            {detail.role === 'donee' ? '本人確認審査詳細' : 'ドナー審査詳細'}
          </h2>
          <p className="mt-1 text-sm text-admin-muted">
            最終更新: {formatDateTime(detail.updatedAt)}
          </p>
        </div>
        <span className={resolveStatusClass(detail.adminReview?.status ?? 'none')}>
          {detail.adminReview ? reviewStatusLabels[detail.adminReview.status] : '審査情報なし'}
        </span>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <article className="rounded-2xl border border-admin-border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold tracking-wide text-admin-muted">申請者情報</h3>
          <div className="mt-3 space-y-1 text-sm">
            <p className="text-base font-semibold text-admin-ink">{detail.displayName}</p>
            <p className="text-admin-muted">{detail.email}</p>
            <p className="text-admin-muted">
              {roleLabels[detail.role]} / {userStatusLabels[detail.userStatus]}
            </p>
            <p className="text-admin-muted">
              {detail.residence ?? '-'}
              {typeof detail.age === 'number' ? ` / ${detail.age}歳` : ''}
            </p>
          </div>
        </article>

        <article className="rounded-2xl border border-admin-border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold tracking-wide text-admin-muted">審査ステータス</h3>
          {detail.adminReview ? (
            <div className="mt-3 space-y-1 text-sm text-admin-muted">
              <p>
                状態: <span className="font-semibold text-admin-ink">{reviewStatusLabels[detail.adminReview.status]}</span>
              </p>
              <p>審査者: {detail.adminReview.reviewedBy ?? '-'}</p>
              <p>審査日時: {formatDateTime(detail.adminReview.reviewedAt)}</p>
              <p>コメント: {detail.adminReview.comment ?? '-'}</p>
            </div>
          ) : (
            <p className="mt-3 text-sm text-admin-muted">審査情報が未作成です。</p>
          )}
        </article>
      </div>

      <div className="space-y-3">
        {VERIFICATION_KEYS.map((type) => {
          const item = detail.items[type];
          const declaredValue =
            type === 'identity'
              ? formatDate(detail.declaredValues.birthDate)
              : type === 'education'
              ? [
                  detail.declaredValues.educationLevel ?? '-',
                  detail.declaredValues.educationSchoolName ?? '-',
                ].join(' / ')
              : type === 'income'
              ? detail.declaredValues.annualIncome ?? '-'
              : '-';
          return (
            <article key={type} className="rounded-2xl border border-admin-border bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-admin-ink">{VERIFICATION_LABELS[type]}</h3>
                <span className={resolveStatusClass(item.status)}>{item.status}</span>
              </div>
              <div className="mt-2 grid gap-1 text-sm text-admin-muted md:grid-cols-2">
                <p>申告値: {declaredValue}</p>
                <p>確認日時: {formatDateTime(item.verifiedAt)}</p>
                <p>有効期限: {formatDateTime(item.expiresAt)}</p>
                <p>ランク: {item.rank ?? '-'}</p>
                <p>却下理由: {item.rejectionReason ?? '-'}</p>
              </div>

              <div className="mt-3">
                <p className="text-sm font-medium text-admin-ink">提出書類 ({item.documents.length}件)</p>
                {item.documents.length === 0 ? (
                  <p className="mt-1 text-sm text-admin-muted">提出書類はありません。</p>
                ) : (
                  <ul className="mt-2 grid gap-3 md:grid-cols-2">
                    {item.documents.map((document, index) => (
                      <li
                        key={`${document.url}-${index}`}
                        className="overflow-hidden rounded-xl border border-admin-border bg-admin-surface text-sm"
                      >
                        {isImageDocument(document.type, document.fileName) ? (
                          <a href={document.url} target="_blank" rel="noreferrer">
                            <img
                              src={document.url}
                              alt={document.fileName}
                              className="h-48 w-full bg-white object-contain"
                            />
                          </a>
                        ) : (
                          <div className="flex h-28 items-center justify-center bg-white px-4 text-xs font-semibold text-admin-muted">
                            PDF / 書類ファイル
                          </div>
                        )}
                        <div className="px-3 py-2">
                        <a
                          href={document.url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-admin-accent hover:underline"
                        >
                          {document.fileName}
                        </a>
                        <p className="mt-1 text-xs text-admin-muted">
                          アップロード: {formatDateTime(document.uploadedAt)}
                          {document.type ? ` / ${document.type}` : ''}
                        </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <article className="rounded-2xl border border-admin-border bg-white p-4 shadow-sm">
        <h3 className="text-base font-semibold text-admin-ink">照合チェック</h3>
        <p className="mt-1 text-sm text-admin-muted">
          承認する場合は必要なチェックをすべて完了してください。
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="flex items-center gap-2 text-sm text-admin-ink">
            <input
              type="checkbox"
              checked={checks.birthDateMatched}
              disabled={!canReview || isSubmitting}
              onChange={(event) =>
                setChecks((prev) => ({ ...prev, birthDateMatched: event.target.checked }))
              }
            />
            生年月日（書類照合）
          </label>
          {detail.role === 'donor' && (
            <>
              <label className="flex items-center gap-2 text-sm text-admin-ink">
                <input
                  type="checkbox"
                  checked={checks.incomeMatched}
                  disabled={!canReview || isSubmitting}
                  onChange={(event) =>
                    setChecks((prev) => ({ ...prev, incomeMatched: event.target.checked }))
                  }
                />
                年収（書類照合）
              </label>
              <label className="flex items-center gap-2 text-sm text-admin-ink">
                <input
                  type="checkbox"
                  checked={checks.educationMatched}
                  disabled={!canReview || isSubmitting}
                  onChange={(event) =>
                    setChecks((prev) => ({ ...prev, educationMatched: event.target.checked }))
                  }
                />
                学歴（書類照合）
              </label>
              <label className="flex items-center gap-2 text-sm text-admin-ink">
                <input
                  type="checkbox"
                  checked={checks.stdTestNegative}
                  disabled={!canReview || isSubmitting}
                  onChange={(event) =>
                    setChecks((prev) => ({ ...prev, stdTestNegative: event.target.checked }))
                  }
                />
                性病検査（陰性確認）
              </label>
            </>
          )}
        </div>

        {detail.role === 'donor' && (
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <div>
              <label className="text-sm font-medium text-admin-ink">学歴ランク</label>
              <select
                value={ranks.education}
                onChange={(event) =>
                  setRanks((prev) => ({ ...prev, education: event.target.value }))
                }
                disabled={!canReview || isSubmitting}
                className="mt-1 w-full rounded-lg border border-admin-border px-3 py-2 text-sm"
              >
                <option value="">選択してください</option>
                {!rankOptions.includes(ranks.education as (typeof rankOptions)[number]) &&
                  ranks.education && (
                    <option value={ranks.education}>{ranks.education} (旧データ)</option>
                  )}
                {rankOptions.map((rank) => (
                  <option key={rank} value={rank}>
                    {rank}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-admin-ink">年収ランク</label>
              <select
                value={ranks.income}
                onChange={(event) =>
                  setRanks((prev) => ({ ...prev, income: event.target.value }))
                }
                disabled={!canReview || isSubmitting}
                className="mt-1 w-full rounded-lg border border-admin-border px-3 py-2 text-sm"
              >
                <option value="">選択してください</option>
                {!rankOptions.includes(ranks.income as (typeof rankOptions)[number]) &&
                  ranks.income && (
                    <option value={ranks.income}>{ranks.income} (旧データ)</option>
                  )}
                {rankOptions.map((rank) => (
                  <option key={rank} value={rank}>
                    {rank}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-admin-ink">精液検査ランク</label>
              <select
                value={ranks.spermTest}
                onChange={(event) =>
                  setRanks((prev) => ({ ...prev, spermTest: event.target.value }))
                }
                disabled={!canReview || isSubmitting}
                className="mt-1 w-full rounded-lg border border-admin-border px-3 py-2 text-sm"
              >
                <option value="">選択してください</option>
                {!rankOptions.includes(ranks.spermTest as (typeof rankOptions)[number]) &&
                  ranks.spermTest && (
                    <option value={ranks.spermTest}>{ranks.spermTest} (旧データ)</option>
                  )}
                {rankOptions.map((rank) => (
                  <option key={rank} value={rank}>
                    {rank}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-admin-ink">性病検査ランク</label>
              <select
                value={ranks.stdTest}
                onChange={(event) =>
                  setRanks((prev) => ({ ...prev, stdTest: event.target.value }))
                }
                disabled={!canReview || isSubmitting}
                className="mt-1 w-full rounded-lg border border-admin-border px-3 py-2 text-sm"
              >
                <option value="">選択してください</option>
                {!rankOptions.includes(ranks.stdTest as (typeof rankOptions)[number]) &&
                  ranks.stdTest && (
                    <option value={ranks.stdTest}>{ranks.stdTest} (旧データ)</option>
                  )}
                {rankOptions.map((rank) => (
                  <option key={rank} value={rank}>
                    {rank}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </article>

      <article className="rounded-2xl border border-admin-border bg-white p-4 shadow-sm">
        <h3 className="text-base font-semibold text-admin-ink">審査コメント</h3>
        <p className="mt-1 text-sm text-admin-muted">
          差し戻し・却下時はコメント必須です。承認時は任意で記録できます。
        </p>
        <textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          rows={4}
          disabled={!canReview || isSubmitting}
          className="mt-3 w-full rounded-xl border border-admin-border bg-white px-3 py-2 text-sm outline-none transition focus:border-admin-accent disabled:cursor-not-allowed disabled:bg-admin-bg"
          placeholder="審査コメントを入力してください"
        />

        {actionError && (
          <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {actionError}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!canReview || isSubmitting}
            onClick={() => void handleDecision('approved')}
            className="rounded-xl bg-admin-success px-4 py-2 text-sm font-semibold text-white transition hover:bg-admin-success/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            承認する
          </button>
          <button
            type="button"
            disabled={!canReview || isSubmitting}
            onClick={() => void handleDecision('remanded')}
            className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-500/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            差し戻す
          </button>
          <button
            type="button"
            disabled={!canReview || isSubmitting}
            onClick={() => void handleDecision('rejected')}
            className="rounded-xl bg-admin-danger px-4 py-2 text-sm font-semibold text-white transition hover:bg-admin-danger/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            却下する
          </button>
          {!canReview && (
            <p className="self-center text-sm text-admin-muted">
              この申請はすでに処理されています。
            </p>
          )}
        </div>
      </article>
    </section>
  );
}

export function ReviewDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  return <ReviewDetailPanel userId={userId} />;
}
